import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChartLine, Binoculars, MagnifyingGlass, ArrowRight } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const tabs = [
  { id: 'brand', label: 'My Brand', path: '/dashboard/brand-analyzer', icon: ChartLine, desc: 'Audit your digital presence and get a health score' },
  { id: 'competitors', label: 'Competitors', path: '/dashboard/competitor-spy', icon: Binoculars, desc: 'Track up to 5 competitors with live data' },
  { id: 'opportunities', label: 'Opportunities', path: '/dashboard/seo', icon: MagnifyingGlass, desc: 'Weekly insights, SEO gaps, and content opportunities' },
];

export default function IntelligenceHubPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState('brand');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="intelligence-hub">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Instrument Serif', serif" }}>Growth Intelligence</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>Know your brand, your competitors, and your opportunities.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          {tabs.map(t => {
            const Icon = t.icon;
            const isActive = active === t.id;
            return (
              <button key={t.id} onClick={() => { setActive(t.id); navigate(t.path); }} data-testid={`intel-tab-${t.id}`}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: isActive ? 'rgba(200,240,96,0.1)' : 'transparent',
                  color: isActive ? '#c8f060' : 'rgba(245,240,232,0.4)',
                  border: isActive ? '1px solid rgba(200,240,96,0.15)' : '1px solid transparent',
                }}>
                <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Cards */}
        <div className="space-y-4">
          {tabs.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.button key={t.id} onClick={() => navigate(t.path)}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="w-full text-left p-5 rounded-xl flex items-start gap-4 transition-all hover:ring-1 hover:ring-white/10 group"
                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(200,240,96,0.06)' }}>
                  <Icon size={20} style={{ color: '#c8f060' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-semibold" style={{ color: '#f5f0e8' }}>{t.label}</p>
                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-50 transition-opacity" style={{ color: '#c8f060' }} />
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{t.desc}</p>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Empty state for competitors */}
        <div className="p-6 rounded-2xl text-center" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Binoculars size={32} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
          <h3 className="text-sm font-semibold mb-1" style={{ color: '#f5f0e8' }}>You haven't tracked any competitors yet</h3>
          <p className="text-xs max-w-sm mx-auto mb-4" style={{ color: 'rgba(245,240,232,0.3)' }}>
            Add a competitor's name or website and we'll show you exactly what they're doing and where your opportunities are.
          </p>
          <button onClick={() => navigate('/dashboard/competitor-spy')} className="px-5 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-2" style={{ background: '#c8f060', color: '#080808' }}>
            Add your first competitor <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
